'use client';

/**
 * The Models admin tab (docs/internal/architecture/model-registry-and-admin-plan.md
 * sections 10.1 and 10.2, lane D, 2026-10-06; docs/adr/0055-models-providers-and-routes-are-data.md).
 *
 * WHY. Owner, 2026-10-05: "define which models are served by which providers
 * with costs and fallback chains" and "admin ui for model selection per
 * provider - listing/delisting". Until the registry, a model, its one supplier
 * and that supplier's cost were rows in lib/models/catalog.ts plus the
 * BEDROCK_ROUTES table and the BEDROCK_ROUTE env, so every change was a deploy
 * and nobody could see why a route was dark. This tab is the readout and the
 * controls for the three tables lane A built (providers, models, routes): one
 * section per provider, that provider's HOME models as rows, and under each
 * model its ordered fallback chain of routes with each provider's own cost.
 *
 * WHAT IT NEVER DOES.
 *  - It never recomputes a margin verdict, a `serviceable` flag or a blocker.
 *    The dots and the "Not serving" line are the admin API's `RouteView`,
 *    computed on the server by lib/models/registry-margin.ts, because the
 *    retail multiplier (MODEL_RETAIL_MULTIPLIER) is a server environment
 *    variable and a browser render would always assume the default (lane A
 *    review, 2026-10-06). Only the 90-day stale badge is computed here
 *    (`isStaleCost`), because it is a badge and never a gate (decision 17.6).
 *    The same holds for the "Serving above retail on the provider's credit
 *    grant" line (ADR 0056, owner 2026-10-06: "we route everything via
 *    bedrock first, right? we should, even if they are slightly more
 *    expensive, we'd burn credits (confirmed by aws)"): it draws the API's
 *    `grantFundedAboveRetail`, and the dots beside it keep showing the loss.
 *  - It has no switch for `models.registry.enabled`. That setting is flipped
 *    in the Settings tab after the rollout steps of plan section 13 (lane A
 *    made it editable there, 2026-10-06). While it is off this tab renders the
 *    static catalog the API returns, read only, under a notice.
 *  - It decides no permission. Staff read and admins write (decision 17.10),
 *    both enforced by the routes; `canWrite` only hides controls, exactly as
 *    the Settings and Ads tabs do.
 *
 * MONEY WORDS (CLAUDE.md credits rule, plan section 9). Provider costs and
 * list prices are genuine USD and are labelled USD per 1M; retail is credits
 * and is labelled credits per 1M; no label puts a dollar name on a credit
 * amount. Prices are stored per 1K as decimal strings (`ModelPricing`); the
 * tab shifts the decimal point AS TEXT (never through a float, which would
 * turn 0.00015 into 0.15000000000000002) to show and accept per 1M, the unit
 * the providers print. The server keeps six decimal places per 1K, so the
 * form refuses a fourth decimal per 1M itself, in the admin's language and
 * units, and lets an optional cache leg be 0 (free) as the server does.
 *
 * STRINGS. Unlike the English-only neighbours, every visible string here is an
 * `admin.models.*` key in en.json, translated in all 15 locales (decision
 * 17.9); tests/unit/components/models-tab-strings.test.ts pins that no JSX
 * text or title is a hardcoded English literal and that every key exists.
 *
 * Fetches use `credentials: 'include', cache: 'no-store'` like the Settings
 * tab: the service worker answers `/api/*` stale-while-revalidate, and a
 * stale readout after a green write is the bug that tab already hit.
 *
 * THE E2E PASS OF 2026-10-06 (scenarios REG-06 to REG-26 of
 * portal-reports/2026-10-06-e2e-visual/scenarios-registry.md) found the tab
 * reading the rows rather than what they mean. What changed, and why:
 *  - REG-14: a model whose PROVIDER is not listed still read "listed" with an
 *    On switch. The status badge now draws the server's `disposition` (the
 *    model's own status still drives the switch, which gets the same title),
 *    and the provider delist confirm counts the models it actually takes out
 *    of the picker (offered ones), not every home model.
 *  - REG-06: on-device models looked like an outage (red dot, "0 of 1",
 *    "no key", a cache pill). They say "on device" instead; the chain never
 *    serves them by design. Count lines are phrased without plurals
 *    ("1 models"), because the translator has no plural forms.
 *  - REG-20: an Approve refusal showed only its first reason; every reason
 *    the server sends is shown, one per line, with a protected refusal's
 *    code constant.
 *  - REG-26: the change history showed raw action codes and no values; each
 *    row now names the action in words and shows old to new.
 *  - REG-23: dates and the tier threshold follow the app's language, not the
 *    browser's; the sync's own problem text (operator text from the price
 *    files and listings) sits under a translated lead line; the Listed
 *    switch is the last column and stays in view while the table scrolls.
 *    Prices keep the decimal point on purpose: the editors take a point.
 *  - REG-07/REG-24: the Wire column has a minimum width (it collapsed to
 *    about 43 px and route rows grew to 360 px), and the header buttons wrap
 *    on a phone instead of running off the card.
 *  - REG-18: a partial sync answered 200 and showed a green "Sync
 *    finished."; it now says it finished with problems, in amber, and the
 *    problems keep their line breaks. A route with no cost no longer says
 *    "none never checked".
 */

import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Activity,
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Cpu,
  Database,
  History,
  Link2,
  ListRestart,
  Pencil,
  Plus,
  RefreshCw,
  ShieldAlert,
  TriangleAlert,
  Undo2,
  Waypoints,
} from 'lucide-react';

import { useTranslation } from '@/components/i18n-provider';
import { Spinner } from '@/components/ui/spinner';
import type { ModelPricing } from '@/lib/models/catalog';
import { isStaleCost, type MarginVerdict, type RouteMargin } from '@/lib/models/registry-margin';
import {
  isBedrockWire,
  routeWire,
  type CacheBilling,
  type ModelDisposition,
  type ProviderKind,
  type RegistryAuditAction,
  type RegistryProbe,
  type RegistrySource,
  type RegistryStatus,
  type RouteCostSource,
  type RouteWire,
  type SkipReason,
} from '@/lib/models/registry-types';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// The admin API's readout (plan section 10.2), as JSON serialises it: every
// timestamp is an ISO string. Lane C serves it; lane D builds against it.
// ---------------------------------------------------------------------------

type Iso = string;
type Translate = (key: string, params?: Record<string, string | number>) => string;

export interface ApiProviderRow {
  key: string;
  label: string;
  kind: ProviderKind;
  keySource: string | null;
  listed: boolean;
  routingEnabled: boolean;
  baseUrl: string | null;
  region?: string | null;
  note?: string | null;
  updatedAt?: Iso | null;
  updatedBy?: string | null;
}

export interface ApiProviderView {
  row: ApiProviderRow;
  keyConfigured: boolean;
  /** Home models of this provider. */
  modelCount: number;
  /** Routes this provider serves, for any model. */
  routeCount: number;
  darkRouteCount: number;
  /** Why this provider may not be delisted (it owns a protected id), or null. */
  protectedReason?: string | null;
}

export interface ApiModelRow {
  modelId: string;
  provider: string;
  source: RegistrySource;
  status: RegistryStatus;
  label: string | null;
  tier?: string | null;
  transport?: string;
  listPrice: ModelPricing | null;
  listPriceSource: string | null;
  listPriceCheckedAt: Iso | null;
  cacheBilling: CacheBilling;
  missingSyncs?: number;
  approvedAt?: Iso | null;
}

export interface ApiRouteRow {
  id: string;
  modelId: string;
  provider: string;
  priority: number;
  enabled: boolean;
  wire: RouteWire | null;
  cost: ModelPricing | null;
  costSource: RouteCostSource | null;
  costVersion: string | null;
  costCheckedAt: Iso | null;
  costNote: string | null;
  reportsCachedTokens: boolean | null;
  probe: RegistryProbe | null;
  source: RegistrySource;
}

export interface ApiRouteView {
  row: ApiRouteRow;
  serviceable: boolean;
  blockers: SkipReason[];
  margin: RouteMargin;
  /** Unix ms or an ISO time; null when the route is not cooling down. */
  cooldownUntil: number | Iso | null;
  keyConfigured: boolean;
  /** The server's 90-day stale-cost badge; the tab computes it only when absent. */
  stale?: boolean;
  /**
   * The server's word that this route serves only because its provider's
   * credit grant pays (ADR 0056, 2026-10-06): its margin dots still show the
   * loss, and the tab adds one line saying why it serves anyway. Never
   * computed here, like every other verdict.
   */
  grantFundedAboveRetail?: boolean;
}

export interface ApiModelView {
  row: ApiModelRow;
  /** The retail the user pays, per 1K, computed by the server (list x the multiplier, cache billing applied). */
  retail: ModelPricing | null;
  disposition: ModelDisposition;
  protectedReason: string | null;
  /** What the code catalog says where it differs from the row (decision 17.7: the table wins, this is the badge). */
  drift: { label?: string; listPrice?: ModelPricing | null } | null;
  routes: ApiRouteView[];
  /** The server's count of serviceable routes; the tab counts `routes` only when absent. */
  serviceableCount?: number;
}

export interface ApiSyncRun {
  id?: string;
  trigger?: string;
  startedAt: Iso;
  finishedAt: Iso | null;
  outcome: 'ok' | 'partial' | 'failed' | null;
  counts: Record<string, number> | null;
  error?: string | null;
}

export interface ApiAuditRow {
  id: string;
  modelId: string | null;
  provider: string | null;
  routeId?: string | null;
  action: string;
  note: string | null;
  createdAt: Iso;
  /** The row before and after (lane C's AuditRow); shapes vary by action, so they are read defensively. */
  oldValue?: unknown;
  newValue?: unknown;
  /** The actor's username (lane C serves `changedByUsername`; `by` is accepted too). */
  changedByUsername?: string | null;
  by?: string | null;
}

export interface ModelsReadout {
  canWrite: boolean;
  enabled: boolean;
  lastRun: ApiSyncRun | null;
  pendingCount: number;
  providers: ApiProviderView[];
  models: ApiModelView[];
  audit?: ApiAuditRow[];
}

// ---------------------------------------------------------------------------
// Decimal strings, per 1K <-> per 1M, as text
// ---------------------------------------------------------------------------

const DECIMAL_RE = /^(\d*)(?:\.(\d*))?$/;

/** Moves the decimal point of a non-negative decimal string; null for anything else. Pure text, no float. */
function shiftDecimal(value: string, places: number): string | null {
  const m = DECIMAL_RE.exec(value.trim());
  if (!m) return null;
  let int = m[1] ?? '';
  let frac = m[2] ?? '';
  if (int === '' && frac === '') return null;
  if (int === '') int = '0';
  if (places >= 0) {
    const padded = frac.padEnd(places, '0');
    int += padded.slice(0, places);
    frac = padded.slice(places);
  } else {
    const p = -places;
    const padded = int.padStart(p + 1, '0');
    frac = padded.slice(padded.length - p) + frac;
    int = padded.slice(0, padded.length - p);
  }
  int = int.replace(/^0+(?=\d)/, '');
  frac = frac.replace(/0+$/, '');
  return frac ? `${int}.${frac}` : int;
}

/** `0.003` per 1K -> `3` per 1M. Null for a missing or malformed leg. */
export function per1kToPer1m(value: string | null | undefined): string | null {
  return typeof value === 'string' ? shiftDecimal(value, 3) : null;
}

/** `3` per 1M -> `0.003` per 1K. Null for anything that is not a plain decimal. */
export function per1mToPer1k(value: string): string | null {
  return shiftDecimal(value, -3);
}

function isPositiveDecimal(value: string): boolean {
  const v = value.trim();
  return DECIMAL_RE.test(v) && v !== '' && v !== '.' && Number(v) > 0;
}

/** Zero or above: an optional cache leg may be free (lane C's parseAdminPricing accepts 0 there). */
function isNonNegativeDecimal(value: string): boolean {
  const v = value.trim();
  return DECIMAL_RE.test(v) && v !== '' && v !== '.' && Number(v) >= 0;
}

/**
 * The server stores at most six decimal places per 1K (registry-admin.ts
 * DECIMAL_RE, the catalog's own precision), which is three per 1M. Checked on
 * the CONVERTED text so trailing zeros never count ("0.1500" per 1M is fine).
 * Without it a rate such as 0.0375 per 1M came back as the server's English
 * error in per-1K units, which contradicts this form (lane D review, 2026-10-06).
 */
export function fitsServerPrecision(per1m: string): boolean {
  const per1k = per1mToPer1k(per1m);
  if (per1k === null) return false;
  const frac = per1k.split('.')[1] ?? '';
  return frac.length <= 6;
}

// ---------------------------------------------------------------------------
// Labels (literal keys, so the strings test can resolve every one)
// ---------------------------------------------------------------------------

const STATUS_KEY: Record<RegistryStatus, string> = {
  pending: 'admin.models.statusPending',
  listed: 'admin.models.statusListed',
  delisted: 'admin.models.statusDelisted',
  retired: 'admin.models.statusRetired',
};

const STATUS_STYLE: Record<RegistryStatus, string> = {
  pending: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  listed: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  delisted: 'bg-muted text-muted-foreground',
  retired: 'bg-destructive/10 text-destructive',
};

const SOURCE_KEY: Record<RegistrySource, string> = {
  catalog: 'admin.models.sourceCatalog',
  bedrock: 'admin.models.sourceSynced',
  admin: 'admin.models.sourceAdded',
};

const BLOCKER_KEY: Record<SkipReason, string> = {
  disabled: 'admin.models.blockerDisabled',
  provider_routing_off: 'admin.models.blockerProviderRoutingOff',
  env: 'admin.models.blockerEnv',
  cost_unconfirmed: 'admin.models.blockerCostUnconfirmed',
  margin: 'admin.models.blockerMargin',
  no_credentials: 'admin.models.blockerNoCredentials',
  cooldown: 'admin.models.blockerCooldown',
  tools: 'admin.models.blockerTools',
};

const LEG_ORDER: (keyof RouteMargin)[] = ['input', 'output', 'cacheRead', 'cacheWrite', 'longContext'];

const LEG_KEY: Record<keyof RouteMargin, string> = {
  input: 'admin.models.legInput',
  output: 'admin.models.legOutput',
  cacheRead: 'admin.models.legCacheRead',
  cacheWrite: 'admin.models.legCacheWrite',
  longContext: 'admin.models.legLongContext',
};

const VERDICT_KEY: Record<MarginVerdict, string> = {
  ok: 'admin.models.verdictOk',
  under: 'admin.models.verdictUnder',
  unknown: 'admin.models.verdictUnknown',
  'n/a': 'admin.models.verdictNa',
};

const VERDICT_STYLE: Record<MarginVerdict, string> = {
  ok: 'bg-emerald-500 border-emerald-500',
  under: 'bg-destructive border-destructive',
  unknown: 'bg-amber-500 border-amber-500',
  'n/a': 'bg-transparent border-muted-foreground/50',
};

const OUTCOME_KEY: Record<'ok' | 'partial' | 'failed', string> = {
  ok: 'admin.models.outcomeOk',
  partial: 'admin.models.outcomePartial',
  failed: 'admin.models.outcomeFailed',
};

/** Each audit action in words (REG-26: the history used to print the raw code). */
const AUDIT_KEY: Record<RegistryAuditAction, string> = {
  discovered: 'admin.models.auditDiscovered',
  price_changed: 'admin.models.auditPriceChanged',
  retired: 'admin.models.auditRetired',
  probe: 'admin.models.auditProbe',
  reconciled: 'admin.models.auditReconciled',
  approved: 'admin.models.auditApproved',
  listed: 'admin.models.auditListed',
  delisted: 'admin.models.auditDelisted',
  list_price_set: 'admin.models.auditListPriceSet',
  cache_billing_set: 'admin.models.auditCacheBillingSet',
  fields_set: 'admin.models.auditFieldsSet',
  route_added: 'admin.models.auditRouteAdded',
  route_enabled: 'admin.models.auditRouteEnabled',
  route_disabled: 'admin.models.auditRouteDisabled',
  route_cost_set: 'admin.models.auditRouteCostSet',
  route_cost_cleared: 'admin.models.auditRouteCostCleared',
  route_wire_set: 'admin.models.auditRouteWireSet',
  chain_reordered: 'admin.models.auditChainReordered',
  provider_listed: 'admin.models.auditProviderListed',
  provider_delisted: 'admin.models.auditProviderDelisted',
  provider_routing_set: 'admin.models.auditProviderRoutingSet',
  seeded: 'admin.models.auditSeeded',
};

const STATUS_ORDER: Record<RegistryStatus, number> = { pending: 0, listed: 1, delisted: 2, retired: 3 };

/** The maker families a Bedrock route can carry (the Bedrock adapters, `BEDROCK_ROUTE_ADAPTERS` minus `native`), plus Bedrock's own models. */
const BEDROCK_CARRIES = new Set(['anthropic', 'openai', 'xai', 'fireworks', 'bedrock']);

const buttonClass =
  'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-border px-3 text-sm hover:bg-muted disabled:opacity-50';
const smallButtonClass =
  'inline-flex h-7 items-center gap-1 whitespace-nowrap rounded-md border border-border px-2 text-xs hover:bg-muted disabled:opacity-50';
const primaryButtonClass =
  'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary px-3 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50';
const inputClass = 'w-full rounded-md border border-border bg-background px-2 py-1 text-sm';

/**
 * The Listed column stays in view while a wide model table scrolls sideways
 * (REG-23: in German the table is 1031 px inside a 930 px box, and the switch
 * started out of view). A sticky cell must be opaque to cover what scrolls
 * under it: the head's colour is the thead tint mixed over the page, and an
 * inset rule marks the edge.
 */
const STICKY_HEAD =
  'sticky right-0 z-[1] bg-[color-mix(in_oklab,var(--color-muted)_40%,var(--color-background))] shadow-[inset_1px_0_0_var(--color-border)]';
const STICKY_CELL = 'sticky right-0 z-[1] bg-background shadow-[inset_1px_0_0_var(--color-border)]';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function legsText(p: ModelPricing | null | undefined, t: Translate): string | null {
  if (!p) return null;
  const parts: string[] = [];
  const add = (key: string, v: string | undefined) => {
    const m = per1kToPer1m(v);
    if (m !== null) parts.push(`${t(key)} ${m}`);
  };
  add('admin.models.legInput', p.inputPer1k);
  add('admin.models.legOutput', p.outputPer1k);
  add('admin.models.legCacheRead', p.cacheReadPer1k);
  add('admin.models.legCacheWrite', p.cacheWritePer1k);
  return parts.length ? parts.join(', ') : null;
}

function tierText(p: ModelPricing | null | undefined, t: Translate, locale: string): string | null {
  const tier = p?.longContext;
  if (!tier) return null;
  const legs = legsText(
    { inputPer1k: tier.inputPer1k, outputPer1k: tier.outputPer1k, cacheReadPer1k: tier.cacheReadPer1k, cacheWritePer1k: tier.cacheWritePer1k },
    t,
  );
  return t('admin.models.tierLine', { threshold: Number(tier.thresholdTokens).toLocaleString(localeTag(locale)), legs: legs ?? '' });
}

/** `3 / 15`: input and output per 1M, the two numbers a row has room for. */
function pairText(p: ModelPricing | null | undefined): string | null {
  if (!p) return null;
  const i = per1kToPer1m(p.inputPer1k);
  const o = per1kToPer1m(p.outputPer1k);
  if (i === null || o === null) return null;
  return `${i} / ${o}`;
}

/**
 * The app's language as a BCP 47 tag for dates and numbers (REG-23: every
 * date here followed the BROWSER's locale, so a German admin on an English
 * browser read 10/6/2026, 9:29:11 AM). Undefined, the browser default, only
 * when the runtime does not know the tag.
 */
function localeTag(locale: string): string | undefined {
  try {
    return Intl.DateTimeFormat.supportedLocalesOf([locale]).length > 0 ? locale : undefined;
  } catch {
    return undefined;
  }
}

function dateText(iso: Iso | null | undefined, locale: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString(localeTag(locale)) : null;
}

function dateTimeText(iso: Iso | null | undefined, locale: string): string {
  const d = new Date(iso ?? '');
  return Number.isFinite(d.getTime()) ? d.toLocaleString(localeTag(locale)) : '';
}

function cooldownTime(until: number | Iso | null, locale: string): string | null {
  if (until == null) return null;
  const ms = typeof until === 'number' ? until : new Date(until).getTime();
  if (!Number.isFinite(ms) || ms <= Date.now()) return null;
  return new Date(ms).toLocaleTimeString(localeTag(locale), { hour: '2-digit', minute: '2-digit' });
}

function wireText(wire: RouteWire | null, t: Translate): string {
  const w = wire ? routeWire({ wire }) : null;
  if (!w) return t('admin.models.wireInvalid');
  if (isBedrockWire(w)) {
    const parts = [w.bedrockId, w.endpoint];
    if (w.basePath) parts.push(w.basePath);
    if (w.profile) parts.push(t('admin.models.wireProfile', { prefix: w.profilePrefix ?? t('admin.models.wireProfileEnv') }));
    return parts.join(', ');
  }
  return w.api ? `${w.providerModelId}, ${w.api}` : w.providerModelId;
}

/**
 * A refusal as the admin should read it, one line per reason (REG-20). The
 * server sends `reasons` with every approval refusal (lane C's
 * `approval_refused`, every reason, the message being the first or a lead
 * line naming it) and `constant` with a protected refusal; this used to show
 * the message alone, so an admin fixed one reason and was refused again for
 * the next. Reasons already inside the message are not repeated.
 */
export function errorText(body: unknown, t: Translate): string | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  let message: string | null = null;
  for (const k of ['error', 'message', 'reason']) {
    if (typeof b[k] === 'string' && b[k]) {
      message = b[k] as string;
      break;
    }
  }
  const lines: string[] = message ? [message] : [];
  const reasons = Array.isArray(b.reasons) ? b.reasons.filter((r): r is string => typeof r === 'string' && r.trim() !== '') : [];
  for (const r of reasons) {
    if (!lines.some((l) => l.includes(r))) lines.push(r);
  }
  const constant = typeof b.constant === 'string' ? b.constant.trim() : '';
  if (constant && !lines.some((l) => l.includes(constant))) lines.push(t('admin.models.protectedTitle', { reason: constant }));
  return lines.length > 0 ? lines.join('\n') : null;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function isPricing(v: unknown): v is ModelPricing {
  const r = asRecord(v);
  return !!r && typeof r.inputPer1k === 'string' && typeof r.outputPer1k === 'string';
}

function clip(s: string, max = 120): string {
  return s.length > max ? `${s.slice(0, max - 3)}...` : s;
}

/**
 * Old to new for one change-history row (REG-26: two "provider_routing_set"
 * rows, off then on, could not be told apart, and a cost edit did not say
 * what it changed). The action already names WHAT changed, so a row shows
 * only the values, in the tab's own words: On/Off, listed, routing, cache
 * billing, status, prices as input / output per 1M, a wire as the Wire
 * column prints it. A row with no old value (a discovery, an added route)
 * shows the new value alone. Shapes vary by action and by the writer
 * (admin, sync, seed), so every value is read defensively and an unknown
 * field is left out rather than printed raw.
 */
export function auditChange(a: Pick<ApiAuditRow, 'oldValue' | 'newValue'>, t: Translate): string | null {
  const oldV = asRecord(a.oldValue);
  const newV = asRecord(a.newValue);
  if (!oldV && !newV) return null;
  const none = t('admin.models.none');
  const bool = (on: string, off: string) => (v: unknown) => (typeof v === 'boolean' ? t(v ? on : off) : null);
  const text = (v: unknown) => (typeof v === 'string' && v.trim() ? clip(v.trim()) : v === null ? none : null);
  const fields: [string, (v: unknown) => string | null][] = [
    ['status', (v) => (typeof v === 'string' && v in STATUS_KEY ? t(STATUS_KEY[v as RegistryStatus]) : null)],
    ['listed', bool('admin.models.providerListed', 'admin.models.providerNotListed')],
    ['routingEnabled', bool('admin.models.routingOn', 'admin.models.routingOff')],
    ['enabled', bool('admin.models.on', 'admin.models.off')],
    ['cacheBilling', (v) => (v === 'input' ? t('admin.models.cacheInput') : v === 'discount' ? t('admin.models.cacheDiscount') : null)],
    ['listPrice', (v) => (v === null ? none : isPricing(v) ? pairText(v) : null)],
    ['cost', (v) => (v === null ? none : isPricing(v) ? pairText(v) : null)],
    ['order', (v) => (Array.isArray(v) ? clip(v.filter((s): s is string => typeof s === 'string').join(', ')) : null)],
    ['wire', (v) => (asRecord(v) ? clip(wireText(v as RouteWire, t)) : null)],
    ['probe', (v) => {
      const p = asRecord(v);
      return p && typeof p.ok === 'boolean' ? t(p.ok ? 'admin.models.probeOk' : 'admin.models.probeFailed') : v === null ? none : null;
    }],
    ['label', text],
    ['tier', text],
    ['marketplaceName', text],
    ['baseUrl', text],
  ];
  const parts: string[] = [];
  for (const [field, fmt] of fields) {
    const o = oldV && field in oldV ? fmt(oldV[field]) : null;
    const n = newV && field in newV ? fmt(newV[field]) : null;
    if (o === null && n === null) continue;
    if (o === n) continue;
    if (!oldV && (n === null || n === none)) continue;
    parts.push(oldV ? `${o ?? none} → ${n ?? none}` : (n as string));
  }
  return parts.length > 0 ? parts.join('; ') : null;
}

/** The seed route answers `{ ok, report: SeedRegistryReport }`; a bare report is accepted too. */
function insertedCount(body: unknown, key: 'providers' | 'models' | 'routes'): number {
  const b = body as { inserted?: Record<string, unknown>; report?: { inserted?: Record<string, unknown> } } | null;
  const inserted = b?.report?.inserted ?? b?.inserted;
  const list = inserted?.[key];
  return Array.isArray(list) ? list.length : 0;
}

/** The sync route answers `{ ok, report: RegistrySyncReport }`; the report's outcome, or null. */
function syncOutcome(body: unknown): 'ok' | 'partial' | 'failed' | null {
  const o = (body as { report?: { outcome?: unknown } } | null)?.report?.outcome;
  return o === 'ok' || o === 'partial' || o === 'failed' ? o : null;
}

const modelUrl = (id: string) => `/api/admin/models/${encodeURIComponent(id)}`;
const routeUrl = (id: string, provider: string) => `${modelUrl(id)}/routes/${encodeURIComponent(provider)}`;
const providerUrl = (key: string) => `/api/admin/models/providers/${encodeURIComponent(key)}`;

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

function Badge({ className, title, children }: { className?: string; title?: string; children: ReactNode }) {
  return (
    <span title={title} className={cn('whitespace-nowrap rounded px-1.5 py-0.5 text-[11px]', className ?? 'bg-muted text-muted-foreground')}>
      {children}
    </span>
  );
}

/** The Ads tab's `role="switch"` pill (components/admin/ads-tab.tsx). */
function Pill({
  on,
  label,
  disabled,
  title,
  onClick,
  testId,
}: {
  on: boolean;
  label: string;
  disabled?: boolean;
  title?: string;
  onClick?: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      title={title}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'whitespace-nowrap rounded-full border px-2 py-0.5 text-xs disabled:cursor-not-allowed disabled:opacity-60',
        on ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-border',
      )}
    >
      {label}
    </button>
  );
}

function MarginDots({ margin, t }: { margin: RouteMargin; t: Translate }) {
  return (
    <div className="flex items-center gap-1" data-testid="margin-dots">
      {LEG_ORDER.map((leg) => {
        const verdict = margin?.[leg] ?? 'unknown';
        return (
          <span
            key={leg}
            data-verdict={verdict}
            title={t('admin.models.marginDotTitle', { leg: t(LEG_KEY[leg]), verdict: t(VERDICT_KEY[verdict]) })}
            className={cn('inline-block h-2.5 w-2.5 rounded-full border', VERDICT_STYLE[verdict])}
          />
        );
      })}
    </div>
  );
}

function RowError({ message, colSpan }: { message: string | undefined; colSpan?: number }) {
  if (!message) return null;
  if (colSpan) {
    return (
      <tr>
        <td colSpan={colSpan} className="px-3 pb-2">
          <p role="alert" className="whitespace-pre-line text-sm text-destructive">
            {message}
          </p>
        </td>
      </tr>
    );
  }
  return (
    <p role="alert" className="whitespace-pre-line px-4 py-2 text-sm text-destructive">
      {message}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Editors
// ---------------------------------------------------------------------------

interface PricingSubmit {
  pricing: ModelPricing;
  checkedAt?: string;
  source?: 'published' | 'admin';
  note: string;
}

function todayInput(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The four legs (and the long-context tier) in USD per 1M as the provider's
 * page prints them, converted to per 1K on submit (plan section 9). Used for a
 * model's list price and for a route's cost; only the route form asks for the
 * checked date and the source. Audio legs of a realtime model are kept as
 * they were: this form does not edit them.
 */
function PricingForm({
  t,
  title,
  initial,
  forRoute,
  busy,
  onCancel,
  onSubmit,
}: {
  t: Translate;
  title: string;
  initial: ModelPricing | null;
  forRoute: boolean;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (input: PricingSubmit) => void;
}) {
  const [input, setInput] = useState(per1kToPer1m(initial?.inputPer1k) ?? '');
  const [output, setOutput] = useState(per1kToPer1m(initial?.outputPer1k) ?? '');
  const [cacheRead, setCacheRead] = useState(per1kToPer1m(initial?.cacheReadPer1k) ?? '');
  const [cacheWrite, setCacheWrite] = useState(per1kToPer1m(initial?.cacheWritePer1k) ?? '');
  const [tiered, setTiered] = useState(!!initial?.longContext);
  const [threshold, setThreshold] = useState(initial?.longContext ? String(initial.longContext.thresholdTokens) : '');
  const [tInput, setTInput] = useState(per1kToPer1m(initial?.longContext?.inputPer1k) ?? '');
  const [tOutput, setTOutput] = useState(per1kToPer1m(initial?.longContext?.outputPer1k) ?? '');
  const [tCacheRead, setTCacheRead] = useState(per1kToPer1m(initial?.longContext?.cacheReadPer1k) ?? '');
  const [tCacheWrite, setTCacheWrite] = useState(per1kToPer1m(initial?.longContext?.cacheWritePer1k) ?? '');
  const [checkedAt, setCheckedAt] = useState(todayInput());
  const [source, setSource] = useState<'published' | 'admin'>('published');
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const submit = () => {
    const required: [string, string][] = [
      [t('admin.models.legInput'), input],
      [t('admin.models.legOutput'), output],
    ];
    const optional: [string, string][] = [
      [t('admin.models.legCacheRead'), cacheRead],
      [t('admin.models.legCacheWrite'), cacheWrite],
    ];
    if (tiered) {
      required.push([t('admin.models.tierField', { leg: t('admin.models.legInput') }), tInput], [t('admin.models.tierField', { leg: t('admin.models.legOutput') }), tOutput]);
      optional.push([t('admin.models.tierField', { leg: t('admin.models.legCacheRead') }), tCacheRead], [t('admin.models.tierField', { leg: t('admin.models.legCacheWrite') }), tCacheWrite]);
    }
    for (const [field, v] of required) {
      if (!isPositiveDecimal(v)) return setProblem(t('admin.models.errNumber', { field }));
      if (!fitsServerPrecision(v)) return setProblem(t('admin.models.errPrecision', { field }));
    }
    // An empty cache leg means "billed at the input rate"; 0 means free. Both are valid.
    for (const [field, v] of optional) {
      if (v.trim() === '') continue;
      if (!isNonNegativeDecimal(v)) return setProblem(t('admin.models.errNumberOptional', { field }));
      if (!fitsServerPrecision(v)) return setProblem(t('admin.models.errPrecision', { field }));
    }
    const thresholdTokens = Number(threshold);
    if (tiered && !(Number.isInteger(thresholdTokens) && thresholdTokens > 0)) return setProblem(t('admin.models.errThreshold'));
    if (!note.trim()) return setProblem(t('admin.models.errNote'));

    const k = (v: string) => per1mToPer1k(v) as string;
    const pricing: ModelPricing = { inputPer1k: k(input), outputPer1k: k(output) };
    if (cacheRead.trim()) pricing.cacheReadPer1k = k(cacheRead);
    if (cacheWrite.trim()) pricing.cacheWritePer1k = k(cacheWrite);
    if (initial?.audioInputPer1k) pricing.audioInputPer1k = initial.audioInputPer1k;
    if (initial?.audioOutputPer1k) pricing.audioOutputPer1k = initial.audioOutputPer1k;
    if (initial?.audioCacheReadPer1k) pricing.audioCacheReadPer1k = initial.audioCacheReadPer1k;
    if (tiered) {
      pricing.longContext = { thresholdTokens, inputPer1k: k(tInput), outputPer1k: k(tOutput) };
      if (tCacheRead.trim()) pricing.longContext.cacheReadPer1k = k(tCacheRead);
      if (tCacheWrite.trim()) pricing.longContext.cacheWritePer1k = k(tCacheWrite);
    }
    setProblem(null);
    const out: PricingSubmit = { pricing, note: note.trim() };
    if (forRoute) {
      out.source = source;
      // Today is the server's "now" (the default); an earlier day is sent as noon UTC of that day.
      if (checkedAt && checkedAt !== todayInput()) out.checkedAt = new Date(`${checkedAt}T12:00:00Z`).toISOString();
    }
    onSubmit(out);
  };

  const field = (label: string, value: string, set: (v: string) => void, testId: string) => (
    <label className="block min-w-[7rem] flex-1 text-xs">
      <span className="mb-1 block text-muted-foreground">{label}</span>
      <input inputMode="decimal" value={value} onChange={(e) => set(e.target.value)} className={cn(inputClass, 'font-mono')} data-testid={testId} />
    </label>
  );

  return (
    <div className="space-y-3" data-testid="pricing-form">
      <div>
        <h4 className="text-sm font-semibold">{title}</h4>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('admin.models.perMillionHint')}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {field(t('admin.models.legInput'), input, setInput, 'price-input')}
        {field(t('admin.models.legOutput'), output, setOutput, 'price-output')}
        {field(t('admin.models.legCacheRead'), cacheRead, setCacheRead, 'price-cache-read')}
        {field(t('admin.models.legCacheWrite'), cacheWrite, setCacheWrite, 'price-cache-write')}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={tiered} onChange={(e) => setTiered(e.target.checked)} />
        {t('admin.models.fieldTiered')}
      </label>
      {tiered ? (
        <div className="flex flex-wrap gap-2">
          {field(t('admin.models.fieldThreshold'), threshold, setThreshold, 'price-threshold')}
          {field(t('admin.models.tierField', { leg: t('admin.models.legInput') }), tInput, setTInput, 'price-tier-input')}
          {field(t('admin.models.tierField', { leg: t('admin.models.legOutput') }), tOutput, setTOutput, 'price-tier-output')}
          {field(t('admin.models.tierField', { leg: t('admin.models.legCacheRead') }), tCacheRead, setTCacheRead, 'price-tier-cache-read')}
          {field(t('admin.models.tierField', { leg: t('admin.models.legCacheWrite') }), tCacheWrite, setTCacheWrite, 'price-tier-cache-write')}
        </div>
      ) : null}
      <div className="flex flex-wrap items-end gap-2">
        {forRoute ? (
          <>
            <label className="block text-xs">
              <span className="mb-1 block text-muted-foreground">{t('admin.models.fieldCheckedAt')}</span>
              <input type="date" value={checkedAt} onChange={(e) => setCheckedAt(e.target.value)} className={inputClass} />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block text-muted-foreground">{t('admin.models.fieldSource')}</span>
              <select value={source} onChange={(e) => setSource(e.target.value as 'published' | 'admin')} className={inputClass}>
                <option value="published">{t('admin.models.sourcePublished')}</option>
                <option value="admin">{t('admin.models.sourceAdminEstimate')}</option>
              </select>
            </label>
          </>
        ) : null}
        <label className="block min-w-[14rem] flex-1 text-xs">
          <span className="mb-1 block text-muted-foreground">{t('admin.models.fieldNote')}</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('admin.models.notePlaceholder')} maxLength={500} className={inputClass} data-testid="price-note" />
        </label>
        <button type="button" onClick={submit} disabled={busy} className={primaryButtonClass} data-testid="price-save">
          {busy ? t('admin.models.saving') : t('admin.models.save')}
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={buttonClass}>
          {t('admin.models.cancel')}
        </button>
      </div>
      {problem ? (
        <p role="alert" className="text-sm text-destructive">
          {problem}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The wire as JSON (admin, rare). Checked with `routeWire` from
 * lib/models/registry-types.ts, the same validator the server and the rails
 * use, before anything is sent; the server checks it again.
 */
function WireForm({
  t,
  title,
  hint,
  initial,
  busy,
  providerChoices,
  onCancel,
  onSubmit,
}: {
  t: Translate;
  title: string;
  hint: string;
  initial: RouteWire | Record<string, unknown> | null;
  busy: boolean;
  /** Present for "Add route": the providers that may carry this model, with a starting wire for each. */
  providerChoices?: { key: string; label: string; template: Record<string, unknown> }[];
  onCancel: () => void;
  onSubmit: (input: { wire: RouteWire; note: string; provider?: string }) => void;
}) {
  const [provider, setProvider] = useState(providerChoices?.[0]?.key ?? '');
  const [text, setText] = useState(() => JSON.stringify(providerChoices?.[0]?.template ?? initial ?? {}, null, 2));
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const pickProvider = (key: string) => {
    setProvider(key);
    const choice = providerChoices?.find((c) => c.key === key);
    if (choice) setText(JSON.stringify(choice.template, null, 2));
  };

  const submit = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return setProblem(t('admin.models.errWire'));
    }
    const wire = routeWire({ wire: parsed as RouteWire });
    if (!wire) return setProblem(t('admin.models.errWire'));
    if (!note.trim()) return setProblem(t('admin.models.errNote'));
    setProblem(null);
    onSubmit({ wire, note: note.trim(), provider: providerChoices ? provider : undefined });
  };

  return (
    <div className="space-y-3" data-testid="wire-form">
      <div>
        <h4 className="text-sm font-semibold">{title}</h4>
        <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
      </div>
      {providerChoices ? (
        <label className="block max-w-xs text-xs">
          <span className="mb-1 block text-muted-foreground">{t('admin.models.fieldProvider')}</span>
          <select value={provider} onChange={(e) => pickProvider(e.target.value)} className={inputClass} data-testid="route-provider">
            {providerChoices.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={7} spellCheck={false} className={cn(inputClass, 'font-mono text-xs')} data-testid="wire-json" />
      <div className="flex flex-wrap items-end gap-2">
        <label className="block min-w-[14rem] flex-1 text-xs">
          <span className="mb-1 block text-muted-foreground">{t('admin.models.fieldNote')}</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} className={inputClass} data-testid="wire-note" />
        </label>
        <button type="button" onClick={submit} disabled={busy} className={primaryButtonClass} data-testid="wire-save">
          {busy ? t('admin.models.saving') : t('admin.models.save')}
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={buttonClass}>
          {t('admin.models.cancel')}
        </button>
      </div>
      {problem ? (
        <p role="alert" className="text-sm text-destructive">
          {problem}
        </p>
      ) : null}
    </div>
  );
}

/** "Attach as route of...": a pending model's Bedrock route moves to a known model (plan section 8.2). */
function AttachForm({
  t,
  model,
  targets,
  busy,
  onCancel,
  onSubmit,
}: {
  t: Translate;
  model: ApiModelView;
  targets: ApiModelView[];
  busy: boolean;
  onCancel: () => void;
  onSubmit: (input: { targetModelId: string; note: string }) => void;
}) {
  const [target, setTarget] = useState(targets[0]?.row.modelId ?? '');
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const name = model.row.label ?? model.row.modelId;
  const submit = () => {
    if (!target) return;
    if (!note.trim()) return setProblem(t('admin.models.errNote'));
    if (!window.confirm(t('admin.models.attachConfirm', { model: name, target }))) return;
    setProblem(null);
    onSubmit({ targetModelId: target, note: note.trim() });
  };
  return (
    <div className="space-y-3" data-testid="attach-form">
      <div>
        <h4 className="text-sm font-semibold">{t('admin.models.attachTitle', { model: name })}</h4>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('admin.models.attachHint')}</p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="block min-w-[14rem] text-xs">
          <span className="mb-1 block text-muted-foreground">{t('admin.models.fieldTarget')}</span>
          <select value={target} onChange={(e) => setTarget(e.target.value)} className={inputClass}>
            {targets.map((m) => (
              <option key={m.row.modelId} value={m.row.modelId}>
                {m.row.label ?? m.row.modelId}
              </option>
            ))}
          </select>
        </label>
        <label className="block min-w-[14rem] flex-1 text-xs">
          <span className="mb-1 block text-muted-foreground">{t('admin.models.fieldNote')}</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} className={inputClass} />
        </label>
        <button type="button" onClick={submit} disabled={busy || !target} className={primaryButtonClass}>
          {busy ? t('admin.models.saving') : t('admin.models.attach')}
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={buttonClass}>
          {t('admin.models.cancel')}
        </button>
      </div>
      {problem ? (
        <p role="alert" className="text-sm text-destructive">
          {problem}
        </p>
      ) : null}
    </div>
  );
}

/**
 * "Clear override": a Bedrock route's admin or published cost goes back to the
 * price list (the next sync sets it). Lane C's patchRoute refuses clearCost
 * without a note (registry-admin.ts, 400 `note_required`, the same rule as
 * setCost and setWire), so this is a small form with the note rather than a
 * bare confirm(), which could never succeed (lane D review, 2026-10-06). The
 * question the confirm used to ask is the form's heading; the button is the yes.
 */
function ClearCostForm({
  t,
  providerLabel,
  modelName,
  busy,
  onCancel,
  onSubmit,
}: {
  t: Translate;
  providerLabel: string;
  modelName: string;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (note: string) => void;
}) {
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const submit = () => {
    if (!note.trim()) return setProblem(t('admin.models.errNote'));
    setProblem(null);
    onSubmit(note.trim());
  };
  return (
    <div className="space-y-3" data-testid="clear-cost-form">
      <h4 className="text-sm font-semibold">{t('admin.models.clearCostConfirm', { provider: providerLabel, model: modelName })}</h4>
      <div className="flex flex-wrap items-end gap-2">
        <label className="block min-w-[14rem] flex-1 text-xs">
          <span className="mb-1 block text-muted-foreground">{t('admin.models.fieldNote')}</span>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('admin.models.notePlaceholder')}
            maxLength={500}
            className={inputClass}
            data-testid="clear-cost-note"
          />
        </label>
        <button type="button" onClick={submit} disabled={busy} className={primaryButtonClass} data-testid="clear-cost-save">
          {busy ? t('admin.models.saving') : t('admin.models.clearCost')}
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={buttonClass}>
          {t('admin.models.cancel')}
        </button>
      </div>
      {problem ? (
        <p role="alert" className="text-sm text-destructive">
          {problem}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------

type Editing =
  | { kind: 'listPrice'; modelId: string }
  | { kind: 'cost'; modelId: string; provider: string }
  | { kind: 'clearCost'; modelId: string; provider: string }
  | { kind: 'wire'; modelId: string; provider: string }
  | { kind: 'addRoute'; modelId: string }
  | { kind: 'attach'; modelId: string };

const MODEL_COLS = 7;
const ROUTE_COLS = 9;

/** The on-device provider and its models: run in the browser, never by the chain (REG-06). */
const isOnDeviceModel = (m: ApiModelView) => m.row.transport === 'local';
const isOnDeviceProvider = (p: Pick<ApiProviderRow, 'kind'> | null | undefined) => p?.kind === 'local';

type Notice = { text: string; tone: 'ok' | 'warn' };

export function ModelsTab() {
  const { t, locale } = useTranslation();
  const [data, setData] = useState<ModelsReadout | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState<Editing | null>(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/models', { credentials: 'include', cache: 'no-store' });
      if (res.status === 403 || res.status === 401) {
        setForbidden(true);
        return;
      }
      if (!res.ok) throw new Error(t('admin.models.loadFailed', { status: res.status }));
      setData((await res.json()) as ModelsReadout);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('admin.models.networkError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const setRowError = useCallback((key: string, message: string | null) => {
    setRowErrors((errs) => {
      const next = { ...errs };
      if (message) next[key] = message;
      else delete next[key];
      return next;
    });
  }, []);

  /**
   * One write: the row's error lands beside the row that failed (the Settings
   * tab's lesson of 2026-09-27), and every success reloads the readout so the
   * blockers, dots and counts are the server's, never a local guess.
   */
  const write = useCallback(
    async (key: string, url: string, method: 'POST' | 'PATCH', body: Record<string, unknown>, onOk?: (json: unknown) => void): Promise<boolean> => {
      setBusy(key);
      setRowError(key, null);
      setNotice(null);
      try {
        const res = await fetch(url, {
          method,
          credentials: 'include',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const json: unknown = await res.json().catch(() => ({}));
        if (!res.ok) {
          setRowError(key, errorText(json, t) ?? t('admin.models.writeFailed', { status: res.status }));
          return false;
        }
        onOk?.(json);
        await load();
        return true;
      } catch {
        setRowError(key, t('admin.models.networkError'));
        return false;
      } finally {
        setBusy(null);
      }
    },
    [load, setRowError, t],
  );

  const providersByKey = useMemo(() => {
    const map = new Map<string, ApiProviderView>();
    for (const p of data?.providers ?? []) map.set(p.row.key, p);
    return map;
  }, [data]);

  const sections = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const byProvider = new Map<string, ApiModelView[]>();
    for (const m of data?.models ?? []) {
      if (q && !m.row.modelId.toLowerCase().includes(q) && !(m.row.label ?? '').toLowerCase().includes(q)) continue;
      const list = byProvider.get(m.row.provider) ?? [];
      list.push(m);
      byProvider.set(m.row.provider, list);
    }
    for (const list of byProvider.values()) {
      list.sort(
        (a, b) =>
          STATUS_ORDER[a.row.status] - STATUS_ORDER[b.row.status] ||
          (a.row.label ?? a.row.modelId).localeCompare(b.row.label ?? b.row.modelId),
      );
    }
    const out: { key: string; view: ApiProviderView | null; models: ApiModelView[] }[] = [];
    for (const p of data?.providers ?? []) {
      const models = byProvider.get(p.row.key) ?? [];
      if (q && models.length === 0) continue;
      out.push({ key: p.row.key, view: p, models });
      byProvider.delete(p.row.key);
    }
    // A model whose home provider has no row still renders (a missing provider
    // row counts as listed and routing, lane A's rule), under its bare key.
    for (const [key, models] of byProvider) out.push({ key, view: null, models });
    return out;
  }, [data, filter]);

  if (forbidden) {
    return (
      <div className="flex items-start gap-3 rounded-lg bg-muted p-4 text-sm">
        <ShieldAlert className="h-5 w-5 shrink-0 text-muted-foreground" />
        <div>{t('admin.models.forbidden')}</div>
      </div>
    );
  }

  const enabled = !!data?.enabled;
  const canWrite = !!data?.canWrite;
  const writable = enabled && canWrite;
  const lastRun = data?.lastRun ?? null;

  // REG-18: the route answers 200 for a PARTIAL run too (only `failed` is a
  // 500), so the outcome decides the notice, never the status alone.
  const syncNow = () =>
    void write('header', '/api/admin/models/sync', 'POST', {}, (json) =>
      setNotice(
        syncOutcome(json) === 'partial' || syncOutcome(json) === 'failed'
          ? { text: t('admin.models.syncPartial'), tone: 'warn' }
          : { text: t('admin.models.syncDone'), tone: 'ok' },
      ),
    );
  const reconcile = () => {
    if (!window.confirm(t('admin.models.reconcileConfirm'))) return;
    void write('header', '/api/admin/models/seed', 'POST', {}, (json) =>
      setNotice({
        text: t('admin.models.reconcileDone', {
          models: insertedCount(json, 'models'),
          routes: insertedCount(json, 'routes'),
          providers: insertedCount(json, 'providers'),
        }),
        tone: 'ok',
      }),
    );
  };

  const toggleProvider = (p: ApiProviderView, field: 'listed' | 'routingEnabled') => {
    const next = !p.row[field];
    if (!next) {
      // REG-14: delisting takes out of the picker only what is OFFERED today;
      // `modelCount` is every home model (delisted, retired and pending too),
      // so the xAI confirm said 9 where 4 left.
      const offered = (data?.models ?? []).filter((m) => m.row.provider === p.row.key && m.disposition === 'offered').length;
      const message =
        field === 'listed'
          ? t('admin.models.providerListedConfirmOff', { provider: p.row.label, models: offered })
          : t('admin.models.providerRoutingConfirmOff', { provider: p.row.label, routes: p.routeCount, models: p.modelCount });
      if (!window.confirm(message)) return;
    }
    void write(`p:${p.row.key}`, providerUrl(p.row.key), 'PATCH', { [field]: next });
  };

  const modelName = (m: ApiModelView) => m.row.label ?? m.row.modelId;

  const toggleListed = (m: ApiModelView) => {
    const listed = m.row.status === 'listed';
    if (listed && !window.confirm(t('admin.models.delistConfirm', { model: modelName(m) }))) return;
    void write(`m:${m.row.modelId}`, modelUrl(m.row.modelId), 'PATCH', { action: listed ? 'delist' : 'list' });
  };

  const toggleCacheBilling = (m: ApiModelView) => {
    const next: CacheBilling = m.row.cacheBilling === 'input' ? 'discount' : 'input';
    const message =
      next === 'input'
        ? t('admin.models.cacheBillingConfirmInput', { model: modelName(m) })
        : t('admin.models.cacheBillingConfirmDiscount', { model: modelName(m) });
    if (!window.confirm(message)) return;
    void write(`m:${m.row.modelId}`, modelUrl(m.row.modelId), 'PATCH', { action: 'setCacheBilling', cacheBilling: next });
  };

  const approve = (m: ApiModelView) => {
    const serving = m.routes.find((r) => r.serviceable) ?? null;
    const message = t('admin.models.approveConfirm', {
      model: modelName(m),
      retail: pairText(m.retail) ?? t('admin.models.none'),
      route: serving ? serving.row.provider : t('admin.models.none'),
      cost: (serving && pairText(serving.row.cost)) ?? t('admin.models.none'),
    });
    if (!window.confirm(message)) return;
    void write(`m:${m.row.modelId}`, modelUrl(m.row.modelId), 'PATCH', { action: 'approve' });
  };

  const move = (m: ApiModelView, index: number, delta: -1 | 1) => {
    const order = m.routes.map((r) => r.row.provider);
    const j = index + delta;
    if (j < 0 || j >= order.length) return;
    [order[index], order[j]] = [order[j] as string, order[index] as string];
    void write(`m:${m.row.modelId}`, modelUrl(m.row.modelId), 'PATCH', { action: 'reorderRoutes', order });
  };

  const toggleRoute = (m: ApiModelView, r: ApiRouteView) => {
    void write(`r:${m.row.modelId}@${r.row.provider}`, routeUrl(m.row.modelId, r.row.provider), 'PATCH', {
      action: r.row.enabled ? 'disable' : 'enable',
    });
  };

  // ClearCostForm collects a note so the audit row says why; the server would store a default note without one.
  const clearCost = (m: ApiModelView, r: ApiRouteView, note: string) => {
    void write(`r:${m.row.modelId}@${r.row.provider}`, routeUrl(m.row.modelId, r.row.provider), 'PATCH', { action: 'clearCost', note }, () =>
      setEditing(null),
    );
  };

  const probe = (m: ApiModelView, r: ApiRouteView) => {
    if (!window.confirm(t('admin.models.probeConfirm', { provider: r.row.provider, model: modelName(m) }))) return;
    void write(`r:${m.row.modelId}@${r.row.provider}`, routeUrl(m.row.modelId, r.row.provider), 'PATCH', { action: 'probe' }, () =>
      setNotice({ text: t('admin.models.probeDone'), tone: 'ok' }),
    );
  };

  /** Providers whose adapter can carry this model's API and that have no route on it yet. */
  const addableProviders = (m: ApiModelView) => {
    const taken = new Set(m.routes.map((r) => r.row.provider));
    const home = m.row.provider;
    const tail = m.row.modelId.split('/').slice(1).join('/') || m.row.modelId;
    const out: { key: string; label: string; template: Record<string, unknown> }[] = [];
    for (const p of data?.providers ?? []) {
      if (taken.has(p.row.key)) continue;
      if (p.row.kind === 'bedrock') {
        if (!BEDROCK_CARRIES.has(home)) continue;
        out.push({
          key: p.row.key,
          label: p.row.label,
          template: { bedrockId: '', endpoint: 'mantle', basePath: '/v1', profile: false, adapter: home === 'bedrock' ? 'native' : home },
        });
      } else if (p.row.key === home) {
        out.push({ key: p.row.key, label: p.row.label, template: { providerModelId: tail } });
      }
    }
    return out;
  };

  return (
    <div className="space-y-6 pb-12">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Cpu className="h-5 w-5" /> {t('admin.models.title')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('admin.models.intro')}</p>
        </div>
        <button type="button" onClick={() => void load()} disabled={loading} className={cn(buttonClass, 'shrink-0')}>
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          {t('admin.models.refresh')}
        </button>
      </div>

      {error ? <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{error}</div> : null}

      {loading && !data ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" tone="muted" />
        </div>
      ) : null}

      {data ? (
        <>
          <section className="rounded-lg border border-border p-4" data-testid="models-header">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge
                className={enabled ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'}
              >
                {enabled ? t('admin.models.registryOn') : t('admin.models.registryOff')}
              </Badge>
              <span className="text-muted-foreground" data-testid="models-last-sync">
                {lastRun
                  ? t('admin.models.lastSync', {
                      when: dateTimeText(lastRun.finishedAt ?? lastRun.startedAt, locale),
                      outcome: lastRun.outcome ? t(OUTCOME_KEY[lastRun.outcome]) : t('admin.models.outcomeRunning'),
                    })
                  : t('admin.models.neverSynced')}
              </span>
              {lastRun?.counts ? (
                <span className="text-xs text-muted-foreground">
                  {t('admin.models.syncCounts', {
                    discovered: lastRun.counts.discovered ?? 0,
                    priced: lastRun.counts.priced ?? 0,
                    added: lastRun.counts.new ?? 0,
                    changes: lastRun.counts.priceChanges ?? 0,
                  })}
                </span>
              ) : null}
              {data.pendingCount > 0 ? (
                <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400">
                  {t('admin.models.pendingCount', { count: data.pendingCount })}
                </Badge>
              ) : null}
              {writable ? (
                // flex-wrap: two nowrap buttons are wider than a 390 px card (REG-24).
                <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                  <button type="button" onClick={syncNow} disabled={busy !== null} className={buttonClass} data-testid="models-sync-now">
                    <RefreshCw className={cn('h-3.5 w-3.5', busy === 'header' && 'animate-spin')} />
                    {t('admin.models.syncNow')}
                  </button>
                  <button type="button" onClick={reconcile} disabled={busy !== null} className={buttonClass} data-testid="models-reconcile">
                    <ListRestart className="h-3.5 w-3.5" />
                    {t('admin.models.reconcile')}
                  </button>
                </div>
              ) : null}
            </div>
            {lastRun?.error ? (
              // The run's problems are operator text from the price files and
              // the listings, one per line (registry-sync/sync.ts joins them
              // with newlines); the lead line is the admin's language (REG-18, REG-23).
              <div className="mt-2 text-xs text-destructive" data-testid="models-sync-errors">
                <p>{t('admin.models.syncErrorsLead')}</p>
                <p className="mt-1 whitespace-pre-line font-mono">{lastRun.error}</p>
              </div>
            ) : null}
            {notice ? (
              <p
                role="status"
                data-testid="models-notice"
                data-tone={notice.tone}
                className={cn('mt-2 text-xs', notice.tone === 'ok' ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400')}
              >
                {notice.text}
              </p>
            ) : null}
            <RowError message={rowErrors.header} />
          </section>

          {!enabled ? (
            <div className="flex items-start gap-3 rounded-lg bg-amber-500/10 p-3 text-sm" data-testid="models-off-notice">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <div>{t('admin.models.offNotice')}</div>
            </div>
          ) : !canWrite ? (
            <div className="flex items-start gap-3 rounded-lg bg-muted p-3 text-sm">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div>{t('admin.models.readOnly')}</div>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t('admin.models.filterPlaceholder')}
              className={cn(inputClass, 'h-8 max-w-xs')}
              data-testid="models-filter"
            />
            <p className="text-xs text-muted-foreground">{t('admin.models.marginLegend')}</p>
          </div>

          {sections.map(({ key, view, models }) => (
            <section key={key} className="rounded-lg border border-border" data-testid={`models-provider-${key}`}>
              <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
                <h3 className="text-sm font-semibold">{view?.row.label ?? key}</h3>
                <code className="text-[11px] text-muted-foreground">{key}</code>
                {view ? (
                  <>
                    {isOnDeviceProvider(view.row) ? (
                      // On-device models need no key (REG-06): never "no key".
                      <Badge>{t('admin.models.onDevice')}</Badge>
                    ) : (
                      <Badge
                        className={
                          view.keyConfigured
                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                            : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                        }
                      >
                        {view.keyConfigured ? t('admin.models.keyConfigured') : t('admin.models.noKey')}
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {t('admin.models.providerCounts', { models: view.modelCount, routes: view.routeCount, dark: view.darkRouteCount })}
                    </span>
                    <div className="ml-auto flex items-center gap-2">
                      <Pill
                        on={view.row.listed}
                        label={view.row.listed ? t('admin.models.providerListed') : t('admin.models.providerNotListed')}
                        disabled={!writable || busy !== null || (view.row.listed && !!view.protectedReason)}
                        title={view.row.listed && view.protectedReason ? t('admin.models.protectedTitle', { reason: view.protectedReason }) : undefined}
                        onClick={() => toggleProvider(view, 'listed')}
                        testId={`provider-listed-${key}`}
                      />
                      <Pill
                        on={view.row.routingEnabled}
                        label={view.row.routingEnabled ? t('admin.models.routingOn') : t('admin.models.routingOff')}
                        disabled={!writable || busy !== null}
                        onClick={() => toggleProvider(view, 'routingEnabled')}
                        testId={`provider-routing-${key}`}
                      />
                    </div>
                  </>
                ) : null}
              </div>
              <RowError message={rowErrors[`p:${key}`]} />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('admin.models.colModel')}</th>
                      <th className="px-3 py-2 font-medium">{t('admin.models.colStatus')}</th>
                      <th className="whitespace-nowrap px-3 py-2 text-right font-medium">{t('admin.models.colListPrice')}</th>
                      <th className="whitespace-nowrap px-3 py-2 text-right font-medium">{t('admin.models.colRetail')}</th>
                      <th className="px-3 py-2 font-medium">{t('admin.models.colCacheBilling')}</th>
                      <th className="px-3 py-2 font-medium">{t('admin.models.colRoutes')}</th>
                      <th className={cn('px-3 py-2 font-medium', STICKY_HEAD)}>{t('admin.models.colListed')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {models.length === 0 ? (
                      <tr>
                        <td colSpan={MODEL_COLS} className="px-3 py-6 text-center text-muted-foreground">
                          {t('admin.models.noModels')}
                        </td>
                      </tr>
                    ) : (
                      models.map((m) => {
                        const id = m.row.modelId;
                        const rowKey = `m:${id}`;
                        const open = !!expanded[id];
                        const serviceable = m.serviceableCount ?? m.routes.filter((r) => r.serviceable).length;
                        const listPriceStale = isStaleCost(m.row.listPriceCheckedAt);
                        const driftParts: string[] = [];
                        if (m.drift?.label) driftParts.push(t('admin.models.driftLabel', { label: m.drift.label }));
                        if (m.drift?.listPrice) driftParts.push(t('admin.models.driftPrice', { price: pairText(m.drift.listPrice) ?? '' }));
                        const listTitle = t('admin.models.priceTitle', {
                          source: m.row.listPriceSource ?? t('admin.models.none'),
                          checked: dateText(m.row.listPriceCheckedAt, locale) ?? t('admin.models.neverChecked'),
                        });
                        const isPending = m.row.status === 'pending';
                        const isRetired = m.row.status === 'retired';
                        const listedLocked = m.row.status === 'listed' && !!m.protectedReason;
                        // REG-14: a listed row under a delisted provider is not offered.
                        // The badge shows what the picker does (the server's
                        // disposition); the switch stays the row's own status.
                        const byProvider = m.row.status === 'listed' && m.disposition === 'delisted';
                        const shownStatus: RegistryStatus = byProvider ? 'delisted' : m.row.status;
                        const onDevice = isOnDeviceModel(m);
                        return (
                          <Fragment key={id}>
                            <tr className="border-t border-border align-top" data-testid={`model-row-${id}`}>
                              <td className="min-w-[13rem] px-3 py-2">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  {/* The disclosure sits before the name, so the Listed
                                      switch can be the last column and stay in view (REG-23). */}
                                  <button
                                    type="button"
                                    onClick={() => setExpanded((e) => ({ ...e, [id]: !open }))}
                                    title={open ? t('admin.models.hideRoutes') : t('admin.models.showRoutes')}
                                    aria-label={open ? t('admin.models.hideRoutes') : t('admin.models.showRoutes')}
                                    aria-expanded={open}
                                    className="-ml-1 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                                    data-testid={`expand-${id}`}
                                  >
                                    {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                  </button>
                                  <span className="font-medium">{m.row.label ?? id}</span>
                                  <Badge>{t(SOURCE_KEY[m.row.source] ?? 'admin.models.sourceCatalog')}</Badge>
                                  {driftParts.length ? (
                                    <Badge
                                      className="bg-amber-500/10 text-amber-600 dark:text-amber-400"
                                      title={t('admin.models.driftTitle', { details: driftParts.join(', ') })}
                                    >
                                      {t('admin.models.drift')}
                                    </Badge>
                                  ) : null}
                                </div>
                                <code className="text-[11px] text-muted-foreground">{id}</code>
                              </td>
                              <td className="px-3 py-2">
                                <span data-testid={`status-${id}`} data-status={shownStatus}>
                                  <Badge className={STATUS_STYLE[shownStatus]} title={byProvider ? t('admin.models.providerDelistedTitle') : undefined}>
                                    {t(STATUS_KEY[shownStatus])}
                                  </Badge>
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums" title={listTitle}>
                                <div className="flex items-center justify-end gap-1">
                                  <span>{pairText(m.row.listPrice) ?? t('admin.models.noPrice')}</span>
                                  {writable ? (
                                    <button
                                      type="button"
                                      onClick={() => setEditing({ kind: 'listPrice', modelId: id })}
                                      disabled={busy !== null}
                                      title={t('admin.models.editListPrice')}
                                      aria-label={t('admin.models.editListPrice')}
                                      className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                                    >
                                      <Pencil className="h-3 w-3" />
                                    </button>
                                  ) : null}
                                </div>
                                {listPriceStale ? <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400">{t('admin.models.stale')}</Badge> : null}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{pairText(m.retail) ?? t('admin.models.noPrice')}</td>
                              <td className="px-3 py-2">
                                {/* An on-device model bills nothing to cache (REG-06). */}
                                {onDevice ? null : (
                                  <Pill
                                    on={m.row.cacheBilling === 'input'}
                                    label={m.row.cacheBilling === 'input' ? t('admin.models.cacheInput') : t('admin.models.cacheDiscount')}
                                    disabled={!writable || busy !== null}
                                    onClick={() => toggleCacheBilling(m)}
                                    title={t('admin.models.cacheBillingTitle')}
                                  />
                                )}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2">
                                {onDevice ? (
                                  // The browser runs it; the chain never does, by design (REG-06).
                                  <span data-testid={`on-device-${id}`}>
                                    <Badge>{t('admin.models.onDevice')}</Badge>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5" title={serviceable === 0 ? t('admin.models.noServiceable') : undefined}>
                                    {serviceable === 0 ? <span className="inline-block h-2 w-2 rounded-full bg-destructive" data-testid="no-serviceable-dot" /> : null}
                                    {t('admin.models.serviceableCount', { serviceable, total: m.routes.length })}
                                  </span>
                                )}
                              </td>
                              <td className={cn('px-3 py-2', STICKY_CELL)}>
                                {isPending ? (
                                  writable ? (
                                    <button type="button" onClick={() => approve(m)} disabled={busy !== null} className={smallButtonClass} data-testid={`approve-${id}`}>
                                      {t('admin.models.approve')}
                                    </button>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">{t('admin.models.awaitingApproval')}</span>
                                  )
                                ) : (
                                  <Pill
                                    on={m.row.status === 'listed'}
                                    label={m.row.status === 'listed' ? t('admin.models.on') : t('admin.models.off')}
                                    disabled={!writable || busy !== null || isRetired || listedLocked}
                                    title={
                                      listedLocked
                                        ? t('admin.models.protectedTitle', { reason: m.protectedReason ?? '' })
                                        : isRetired
                                          ? t('admin.models.retiredTitle')
                                          : byProvider
                                            ? t('admin.models.providerDelistedTitle')
                                            : undefined
                                    }
                                    onClick={() => toggleListed(m)}
                                    testId={`listed-${id}`}
                                  />
                                )}
                              </td>
                            </tr>
                            <RowError message={rowErrors[rowKey]} colSpan={MODEL_COLS} />
                            {editing?.kind === 'listPrice' && editing.modelId === id ? (
                              <tr className="bg-muted/10">
                                <td colSpan={MODEL_COLS} className="p-4">
                                  <PricingForm
                                    t={t}
                                    title={t('admin.models.listPriceTitle', { model: modelName(m) })}
                                    initial={m.row.listPrice}
                                    forRoute={false}
                                    busy={busy !== null}
                                    onCancel={() => setEditing(null)}
                                    onSubmit={(input) =>
                                      void write(rowKey, modelUrl(id), 'PATCH', { action: 'setListPrice', listPrice: input.pricing, note: input.note }, () =>
                                        setEditing(null),
                                      )
                                    }
                                  />
                                </td>
                              </tr>
                            ) : null}
                            {open ? (
                              <tr className="bg-muted/10">
                                <td colSpan={MODEL_COLS} className="px-3 pb-3 pt-1">
                                  <ChainTable
                                    t={t}
                                    locale={locale}
                                    model={m}
                                    writable={writable}
                                    busy={busy}
                                    rowErrors={rowErrors}
                                    editing={editing}
                                    setEditing={setEditing}
                                    providersByKey={providersByKey}
                                    addableProviders={addableProviders(m)}
                                    attachTargets={(data.models ?? []).filter(
                                      (x) => x.row.modelId !== id && (x.row.status === 'listed' || x.row.status === 'delisted'),
                                    )}
                                    onMove={(index, delta) => move(m, index, delta)}
                                    onToggle={(r) => toggleRoute(m, r)}
                                    onClearCost={(r, note) => clearCost(m, r, note)}
                                    onProbe={(r) => probe(m, r)}
                                    onSetCost={(r, input) =>
                                      void write(
                                        `r:${id}@${r.row.provider}`,
                                        routeUrl(id, r.row.provider),
                                        'PATCH',
                                        { action: 'setCost', cost: input.pricing, checkedAt: input.checkedAt, source: input.source, note: input.note },
                                        () => setEditing(null),
                                      )
                                    }
                                    onSetWire={(r, input) =>
                                      void write(`r:${id}@${r.row.provider}`, routeUrl(id, r.row.provider), 'PATCH', { action: 'setWire', wire: input.wire, note: input.note }, () =>
                                        setEditing(null),
                                      )
                                    }
                                    onAddRoute={(input) =>
                                      void write(rowKey, `${modelUrl(id)}/routes`, 'POST', { provider: input.provider, wire: input.wire, note: input.note }, () =>
                                        setEditing(null),
                                      )
                                    }
                                    onAttach={(input) =>
                                      void write(rowKey, modelUrl(id), 'PATCH', { action: 'attachRouteTo', targetModelId: input.targetModelId, note: input.note }, () =>
                                        setEditing(null),
                                      )
                                    }
                                  />
                                </td>
                              </tr>
                            ) : null}
                          </Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          ))}

          <div>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <History className="h-4 w-4" /> {t('admin.models.historyTitle')}
            </h3>
            {data.audit?.length ? (
              <div className="card divide-y divide-border">
                {data.audit.map((a) => {
                  const actionKey = AUDIT_KEY[a.action as RegistryAuditAction] as string | undefined;
                  const change = auditChange(a, t);
                  return (
                    <div key={a.id} className="flex flex-wrap items-start justify-between gap-3 p-3 text-sm" data-testid={`audit-${a.id}`}>
                      <div className="min-w-0">
                        {/* An action this build has no words for yet keeps its code. */}
                        {actionKey ? <div className="font-medium">{t(actionKey)}</div> : <code className="text-[11px] text-muted-foreground">{a.action}</code>}
                        <div className="mt-0.5 font-mono text-xs text-muted-foreground">{[a.modelId, a.provider].filter(Boolean).join(' @ ')}</div>
                        {change ? (
                          <div className="mt-0.5 font-mono text-xs [overflow-wrap:anywhere]" data-testid={`audit-change-${a.id}`}>
                            {change}
                          </div>
                        ) : null}
                        {a.note ? <div className="mt-1 text-muted-foreground">{a.note}</div> : null}
                      </div>
                      <div className="shrink-0 text-right text-xs text-muted-foreground">
                        {a.changedByUsername || a.by ? <div>@{a.changedByUsername ?? a.by}</div> : null}
                        <div>{dateTimeText(a.createdAt, locale)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-start gap-3 rounded-lg bg-muted p-3 text-sm text-muted-foreground">
                <Database className="mt-0.5 h-4 w-4 shrink-0" />
                <div>{t('admin.models.noHistory')}</div>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One model's chain of routes
// ---------------------------------------------------------------------------

function ChainTable({
  t,
  locale,
  model,
  writable,
  busy,
  rowErrors,
  editing,
  setEditing,
  providersByKey,
  addableProviders,
  attachTargets,
  onMove,
  onToggle,
  onClearCost,
  onProbe,
  onSetCost,
  onSetWire,
  onAddRoute,
  onAttach,
}: {
  t: Translate;
  locale: string;
  model: ApiModelView;
  writable: boolean;
  busy: string | null;
  rowErrors: Record<string, string>;
  editing: Editing | null;
  setEditing: (e: Editing | null) => void;
  providersByKey: Map<string, ApiProviderView>;
  addableProviders: { key: string; label: string; template: Record<string, unknown> }[];
  attachTargets: ApiModelView[];
  onMove: (index: number, delta: -1 | 1) => void;
  onToggle: (r: ApiRouteView) => void;
  onClearCost: (r: ApiRouteView, note: string) => void;
  onProbe: (r: ApiRouteView) => void;
  onSetCost: (r: ApiRouteView, input: PricingSubmit) => void;
  onSetWire: (r: ApiRouteView, input: { wire: RouteWire; note: string }) => void;
  onAddRoute: (input: { wire: RouteWire; note: string; provider?: string }) => void;
  onAttach: (input: { targetModelId: string; note: string }) => void;
}) {
  const id = model.row.modelId;
  const name = model.row.label ?? id;
  const enabledCount = model.routes.filter((r) => r.row.enabled).length;
  const isBusy = busy !== null;

  return (
    // `w-0 min-w-full`: the chain contributes no width of its own to the
    // provider table it is nested in, then fills the cell, so its own
    // overflow box scrolls instead. WHY (visual re-check of 2026-10-07,
    // REG-07): with Grok 4.6 expanded the xAI table grew to 1124 px inside a
    // 930 px box, the sticky Listed header sat on the Cache header and the
    // Routes column went off the right edge.
    <div className="w-0 min-w-full space-y-2" data-testid={`chain-${id}`}>
      <div className="overflow-x-auto rounded-md border border-border bg-background">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-2 py-2 font-medium">{t('admin.models.colOrder')}</th>
              <th className="px-2 py-2 font-medium">{t('admin.models.colEnabled')}</th>
              <th className="px-2 py-2 font-medium">{t('admin.models.colProvider')}</th>
              <th className="px-2 py-2 font-medium">{t('admin.models.colWire')}</th>
              <th className="px-2 py-2 font-medium">{t('admin.models.colCost')}</th>
              <th className="px-2 py-2 font-medium">{t('admin.models.colMargin')}</th>
              <th className="px-2 py-2 font-medium">{t('admin.models.colProbe')}</th>
              <th className="px-2 py-2 font-medium">{t('admin.models.colCooldown')}</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {model.routes.length === 0 ? (
              <tr>
                <td colSpan={ROUTE_COLS} className="px-3 py-4 text-center text-muted-foreground">
                  {t('admin.models.noRoutes')}
                </td>
              </tr>
            ) : (
              model.routes.map((r, index) => {
                const routeKey = `r:${id}@${r.row.provider}`;
                // An on-device route is never served by the chain, by design: no
                // "no key", no blocker line, no dimming (REG-06).
                const onDeviceRoute = isOnDeviceProvider(providersByKey.get(r.row.provider)?.row) || r.row.provider === 'local';
                const blockers = onDeviceRoute ? [] : (r.blockers ?? []).map((b) => t(BLOCKER_KEY[b] ?? 'admin.models.blockerDisabled'));
                const blockerLine = blockers.length ? t('admin.models.notServing', { reasons: blockers.join(', ') }) : null;
                const lastEnabledProtected = !!model.protectedReason && r.row.enabled && enabledCount === 1;
                const costStale = r.stale ?? isStaleCost(r.row.costCheckedAt);
                const costTitle = t('admin.models.costTitle', {
                  source: r.row.costSource ?? t('admin.models.none'),
                  version: r.row.costVersion ?? t('admin.models.none'),
                  checked: dateText(r.row.costCheckedAt, locale) ?? t('admin.models.neverChecked'),
                  note: r.row.costNote ?? '',
                });
                const providerLabel = providersByKey.get(r.row.provider)?.row.label ?? r.row.provider;
                const overridden = r.row.provider === 'bedrock' && (r.row.costSource === 'admin' || r.row.costSource === 'published');
                const until = cooldownTime(r.cooldownUntil, locale);
                const probe = r.row.probe;
                const cacheReports =
                  r.row.reportsCachedTokens === true
                    ? t('admin.models.cacheYes')
                    : r.row.reportsCachedTokens === false
                      ? t('admin.models.cacheNo')
                      : t('admin.models.cacheUnknown');
                return (
                  <Fragment key={r.row.id || r.row.provider}>
                    <tr
                      className={cn('border-t border-border align-top', !r.serviceable && !onDeviceRoute && 'text-muted-foreground')}
                      title={blockerLine ?? undefined}
                      data-testid={`route-row-${id}@${r.row.provider}`}
                    >
                      <td className="whitespace-nowrap px-2 py-2">
                        <div className="flex items-center gap-0.5">
                          <span className="w-4 text-xs tabular-nums">{r.row.priority}</span>
                          {writable ? (
                            <>
                              <button
                                type="button"
                                onClick={() => onMove(index, -1)}
                                disabled={isBusy || index === 0}
                                title={t('admin.models.moveUp')}
                                aria-label={t('admin.models.moveUp')}
                                className="rounded p-1 hover:bg-accent disabled:opacity-30"
                                data-testid={`move-up-${id}@${r.row.provider}`}
                              >
                                <ArrowUp className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => onMove(index, 1)}
                                disabled={isBusy || index === model.routes.length - 1}
                                title={t('admin.models.moveDown')}
                                aria-label={t('admin.models.moveDown')}
                                className="rounded p-1 hover:bg-accent disabled:opacity-30"
                                data-testid={`move-down-${id}@${r.row.provider}`}
                              >
                                <ArrowDown className="h-3 w-3" />
                              </button>
                            </>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <Pill
                          on={r.row.enabled}
                          label={r.row.enabled ? t('admin.models.on') : t('admin.models.off')}
                          disabled={!writable || isBusy || lastEnabledProtected}
                          title={lastEnabledProtected ? t('admin.models.protectedTitle', { reason: model.protectedReason ?? '' }) : undefined}
                          onClick={() => onToggle(r)}
                          testId={`route-enabled-${id}@${r.row.provider}`}
                        />
                      </td>
                      <td className="whitespace-nowrap px-2 py-2">
                        <div>{providerLabel}</div>
                        {onDeviceRoute ? (
                          <div className="text-[11px] text-muted-foreground">{t('admin.models.onDevice')}</div>
                        ) : !r.keyConfigured ? (
                          <div className="text-[11px] text-amber-600 dark:text-amber-400">{t('admin.models.noKey')}</div>
                        ) : null}
                      </td>
                      {/* A minimum width (REG-07, REG-24): with break-all and none, auto
                          layout took every pixel from this cell, about 43 px, and a
                          Bedrock row grew to 360 px. Ids now break at punctuation first. */}
                      <td className="min-w-[14rem] px-2 py-2" data-testid={`route-wire-${id}@${r.row.provider}`}>
                        <code className="text-[11px] [overflow-wrap:anywhere]">{wireText(r.row.wire, t)}</code>
                      </td>
                      <td className="min-w-[11rem] px-2 py-2 text-xs tabular-nums" title={costTitle}>
                        <div>{legsText(r.row.cost, t) ?? t('admin.models.noPrice')}</div>
                        {tierText(r.row.cost, t, locale) ? <div className="text-muted-foreground">{tierText(r.row.cost, t, locale)}</div> : null}
                        {/* No cost, nothing to date: it used to read "none never checked" (REG-18). */}
                        {r.row.cost ? (
                          <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground" data-testid={`route-cost-meta-${id}@${r.row.provider}`}>
                            <span>{r.row.costSource ?? t('admin.models.none')}</span>
                            <span>{dateText(r.row.costCheckedAt, locale) ?? t('admin.models.neverChecked')}</span>
                            {costStale ? <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400">{t('admin.models.stale')}</Badge> : null}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-2 py-2">
                        <MarginDots margin={r.margin} t={t} />
                        {r.grantFundedAboveRetail ? (
                          <div className="mt-1 max-w-[10rem] text-[11px] leading-tight text-amber-600 dark:text-amber-400" data-testid={`route-grant-${id}@${r.row.provider}`}>
                            {t('admin.models.grantFundedAboveRetail')}
                          </div>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-xs">
                        <div>
                          {probe
                            ? t('admin.models.probeResult', {
                                result: probe.ok ? t('admin.models.probeOk') : t('admin.models.probeFailed'),
                                when: dateText(probe.at, locale) ?? '',
                              })
                            : t('admin.models.probeNever')}
                        </div>
                        <div className="text-[11px] text-muted-foreground">{t('admin.models.reportsCache', { value: cacheReports })}</div>
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-xs">
                        {until ? <span className="text-amber-600 dark:text-amber-400">{t('admin.models.cooldownUntil', { time: until })}</span> : t('admin.models.none')}
                      </td>
                      <td className="px-2 py-2">
                        {writable ? (
                          <div className="flex flex-wrap justify-end gap-1">
                            <button type="button" onClick={() => setEditing({ kind: 'cost', modelId: id, provider: r.row.provider })} disabled={isBusy} className={smallButtonClass}>
                              <Pencil className="h-3 w-3" />
                              {t('admin.models.editCost')}
                            </button>
                            {overridden ? (
                              <button
                                type="button"
                                onClick={() => setEditing({ kind: 'clearCost', modelId: id, provider: r.row.provider })}
                                disabled={isBusy}
                                className={smallButtonClass}
                                data-testid={`clear-cost-${id}@${r.row.provider}`}
                              >
                                <Undo2 className="h-3 w-3" />
                                {t('admin.models.clearCost')}
                              </button>
                            ) : null}
                            <button type="button" onClick={() => onProbe(r)} disabled={isBusy} className={smallButtonClass}>
                              <Activity className="h-3 w-3" />
                              {t('admin.models.probe')}
                            </button>
                            <button type="button" onClick={() => setEditing({ kind: 'wire', modelId: id, provider: r.row.provider })} disabled={isBusy} className={smallButtonClass}>
                              <Waypoints className="h-3 w-3" />
                              {t('admin.models.setWire')}
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                    {!r.serviceable && blockerLine ? (
                      <tr>
                        <td colSpan={ROUTE_COLS} className="px-2 pb-2 text-xs text-muted-foreground" data-testid={`route-blockers-${id}@${r.row.provider}`}>
                          {blockerLine}
                        </td>
                      </tr>
                    ) : null}
                    <RowError message={rowErrors[routeKey]} colSpan={ROUTE_COLS} />
                    {editing?.kind === 'cost' && editing.modelId === id && editing.provider === r.row.provider ? (
                      <tr className="bg-muted/10">
                        <td colSpan={ROUTE_COLS} className="p-3">
                          <PricingForm
                            t={t}
                            title={t('admin.models.costFormTitle', { provider: providerLabel, model: name })}
                            initial={r.row.cost}
                            forRoute
                            busy={isBusy}
                            onCancel={() => setEditing(null)}
                            onSubmit={(input) => onSetCost(r, input)}
                          />
                        </td>
                      </tr>
                    ) : null}
                    {editing?.kind === 'clearCost' && editing.modelId === id && editing.provider === r.row.provider ? (
                      <tr className="bg-muted/10">
                        <td colSpan={ROUTE_COLS} className="p-3">
                          <ClearCostForm
                            t={t}
                            providerLabel={providerLabel}
                            modelName={name}
                            busy={isBusy}
                            onCancel={() => setEditing(null)}
                            onSubmit={(note) => onClearCost(r, note)}
                          />
                        </td>
                      </tr>
                    ) : null}
                    {editing?.kind === 'wire' && editing.modelId === id && editing.provider === r.row.provider ? (
                      <tr className="bg-muted/10">
                        <td colSpan={ROUTE_COLS} className="p-3">
                          <WireForm
                            t={t}
                            title={t('admin.models.wireFormTitle', { provider: providerLabel, model: name })}
                            hint={t('admin.models.wireHint')}
                            initial={r.row.wire}
                            busy={isBusy}
                            onCancel={() => setEditing(null)}
                            onSubmit={(input) => onSetWire(r, input)}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {writable ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setEditing({ kind: 'addRoute', modelId: id })}
            disabled={isBusy || addableProviders.length === 0}
            title={addableProviders.length === 0 ? t('admin.models.noAddableProviders') : undefined}
            className={smallButtonClass}
            data-testid={`add-route-${id}`}
          >
            <Plus className="h-3 w-3" />
            {t('admin.models.addRoute')}
          </button>
          {model.row.status === 'pending' ? (
            <button
              type="button"
              onClick={() => setEditing({ kind: 'attach', modelId: id })}
              disabled={isBusy || attachTargets.length === 0}
              className={smallButtonClass}
              data-testid={`attach-${id}`}
            >
              <Link2 className="h-3 w-3" />
              {t('admin.models.attach')}
            </button>
          ) : null}
        </div>
      ) : null}

      {editing?.kind === 'addRoute' && editing.modelId === id && addableProviders.length > 0 ? (
        <div className="rounded-md border border-border bg-background p-3">
          <WireForm
            t={t}
            title={t('admin.models.addRouteTitle', { model: name })}
            hint={t('admin.models.addRouteHint')}
            initial={null}
            busy={isBusy}
            providerChoices={addableProviders}
            onCancel={() => setEditing(null)}
            onSubmit={onAddRoute}
          />
        </div>
      ) : null}

      {editing?.kind === 'attach' && editing.modelId === id ? (
        <div className="rounded-md border border-border bg-background p-3">
          <AttachForm t={t} model={model} targets={attachTargets} busy={isBusy} onCancel={() => setEditing(null)} onSubmit={onAttach} />
        </div>
      ) : null}
    </div>
  );
}
